import React, { useEffect, ReactNode, useState } from "react";
import Hamburger from "./Hamburger";
import { useDispatch, useSelector } from "react-redux";
import { addTabs, setShowTabs, setDisabledTabs } from "../store/state/SwitchTabs";
import { FilterTab } from "./Filters";
import { DynamicDatePicker } from "./DatePicker";
import { SearchBox } from "./Search";
import {
  setDefaultDates,
  updateDefaultDates,
  updateDefaultTrendDates,
} from "../store/state/DefaultDatesSetter";
import { formatDateForAPI } from "../pages/Rajasthan/const";

interface TabConfig {
  [key: string]: ReactNode; // Tab name as key and its corresponding component as value
}

interface DynamicTabProps {
  headerPrefixComponent?: ReactNode;
  header: string; // The header text
  tabs: TabConfig; // Tab configuration object
  showTabs?: boolean; // Optional flag to show/hide tabs
  middleComponent?: ReactNode; // Optional component to render between header and tabs
  trendTabs?: string[];
  disabledTabs?: string[];
}

interface NewDynamicTabProps {
  headerPrefixComponent?: ReactNode;
  header: string; // The header text
  tabs: TabConfig; // Tab configuration object
  showTabs?: boolean; // Optional flag to show/hide tabs
  middleComponent?: ReactNode; // Optional component to render between header and tabs
  trendTabs?: string[];
  defaultDates?: Record<string, any>;
  hideDateFilterOnTabs?: string[];
  showDates?: boolean;
  minDate?: string;
  headerHeight?: string;
  showReactSelect?: boolean;
  disabledTabs?: string[];
}

export function DynamicTab({
  headerPrefixComponent,
  header,
  tabs,
  middleComponent,
  showTabs = true,
  disabledTabs = [],

}: DynamicTabProps) {
  const tabNames = Object.keys(tabs); // Extract tab names from the config
  const initialTab =
    new URLSearchParams(window.location.search).get("tab") || tabNames[0];

  const [selectedTab, setSelectedTab] = useState(initialTab);

  // Handle tab change and update URL
  const handleTabChange = (option: string) => {
    // Check if the tab is disabled
    if (disabledTabs.includes(option)) {
      return; // Do nothing if tab is disabled
    }
    setSelectedTab(option);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", option);
    window.history.pushState({}, "", url);
  };

  useEffect(() => {
    // Update selected tab if URL changes
    const handlePopState = () => {
      const tab = new URLSearchParams(window.location.search).get("tab");
      if (tab && tabNames.includes(tab)) {
        setSelectedTab(tab);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [tabNames]);

  return (
    <div className="page-wrapper">
      <div className="header flex items-center justify-between">
        <div className="flex items-center gap-5">
          <Hamburger />
          {headerPrefixComponent}

          <h1 className="text-[18px] text-[#6F6D7C]">{header}</h1>
        </div>
        <h1>{middleComponent}</h1>
        {showTabs ? (
          tabNames.length === 1 ? (
            <div className="tab tab-active rounded-lg	">{tabNames[0]}</div>
          ) : (
            <FilterTab
              options={tabNames}
              selected={[selectedTab]}
              onClick={handleTabChange}
              disabledOptions={disabledTabs}
            />
          )
        ) : (
          <div className="w-40"></div>
        )}
      </div>

      {/* Render the selected tab's component */}
      <div className="tabContent">
        <div className="flex flex-row w-full h-full">
          {tabs[selectedTab] || <div>Invalid tab selected</div>}
        </div>
      </div>
    </div>
  );
}

export function NewDynamicTab({
  headerHeight,
  headerPrefixComponent,
  hideDateFilterOnTabs = [],
  header,
  tabs,
  middleComponent,
  showDates = true,
  trendTabs,
  defaultDates,
  showTabs = true,
  showReactSelect = false,
  minDate,
  disabledTabs = []

}: NewDynamicTabProps) {
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (defaultDates) {
      dispatch(setDefaultDates(defaultDates));
    }
  }, [dispatch, defaultDates]);

  // sets default tabs
  useEffect(() => {
    if (tabs && Object.keys(tabs).length > 0) {
      dispatch(addTabs(tabs)); // only pass tab data
      dispatch(setShowTabs(showTabs)); // control visibility separately
      dispatch(setDisabledTabs(disabledTabs)); // set disabled tabs
    }
  }, [dispatch, showTabs, tabs, disabledTabs]);

  function addDate(date: any) {
    // Format the new date range in the same format used for storage
    const newDateLabel = `${formatDateForAPI(
      date.startDate
    )} to ${formatDateForAPI(date.endDate)}`;
    const newDateValue = `${formatDateForAPI(
      date.startDate
    )},${formatDateForAPI(date.endDate)}`;

    // Get existing trend dates
    const existingTrendDates = DefaultDates[activeTab]?.defaultTrendDates || [];

    // Check if this exact date range already exists
    const dateRangeExists = existingTrendDates.some(
      (existingDate: any) => existingDate.value === newDateValue
    );

    // Only add the date if it doesn't already exist
    if (!dateRangeExists) {
      const aa = existingTrendDates.concat([
        {
          label: newDateLabel,
          value: newDateValue,
        },
      ]);

      let dates = [...aa];
      let i = 0;
      while (i < dates.length) {
        if (!dates[i].label.includes("to")) {
          const labelToRemove = dates[i].label;
          dates = dates.filter((d: any) => d.label !== labelToRemove);
        } else {
          i++;
        }
      }
      dispatch(updateDefaultTrendDates({ activeTab, dates }));
    }
  }

  const shouldShowDateFilter = !hideDateFilterOnTabs.includes(activeTab);

  function showDateRange() {
    if (DefaultDates && DefaultDates[activeTab]) {
      return DefaultDates[activeTab].showDateRange || false;
    }
    return false;
  }

  return (
    <div className="page-wrapper">
      <div
        className={`newheader grid grid-cols-3 gap-3 justify-between items-center relative z-[99] ${headerHeight}`}
      >
        <div className="col-span-1">
          <div className="flex items-center gap-5">
            <Hamburger />
            {headerPrefixComponent}

            <h1 className="text-[18px] text-[#6F6D7C]">{header}</h1>
          </div>
        </div>
        <div className="col-span-1">
          <h1>{middleComponent}</h1>
        </div>

        <div className="col-span-1 flex justify-end">
          {showDates && DefaultDates && shouldShowDateFilter && (
            <>
              {trendTabs?.includes(activeTab) ? (
                <div className="flex items-center gap-2 ">
                  <DynamicDatePicker setDate={(date) => addDate(date[0]!)!} />

                  <SearchBox
                    options={DefaultDates[activeTab]?.trendDateOptions || []}
                    isMany={true}
                    placeholder="Date Range"
                    selectedOptions={
                      DefaultDates[activeTab]?.defaultTrendDates || []
                    }
                    onChange={(item: any) => {
                      let dates = [...item];
                      let i = 0;
                      while (i < dates.length) {
                        if (dates[i].label.includes("to")) {
                          const labelToRemove = dates[i].label;
                          dates = dates.filter(
                            (d: any) => d.label !== labelToRemove
                          );
                        } else {
                          i++;
                        }
                      }

                      dispatch(
                        updateDefaultTrendDates({
                          activeTab,
                          dates: dates,
                        })
                      );
                    }}
                  />
                </div>
              ) : (
                <>
                  {!showDateRange() ? (
                    <DynamicDatePicker
                      setDate={(date) => {
                        dispatch(
                          updateDefaultDates({ activeTab, dates: date[0]! })
                        );
                      }}
                      minDate={minDate}
                      defaultDates={
                        DefaultDates[activeTab]?.defaultRangeDate || []
                      }
                    />
                  ) : (
                    <SearchBox
                      options={
                        DefaultDates[activeTab]?.dateRangeDateOptions || []
                      }
                      isMany={false}
                      placeholder="Date Range"
                      selectedOptions={[
                        DefaultDates[activeTab]?.defaultRangeDate,
                      ]}
                      onChange={(item: any) => {
                        dispatch(
                          updateDefaultDates({
                            activeTab,
                            dates: item,
                          })
                        );
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}

          {showReactSelect && DefaultDates && shouldShowDateFilter && (
            <SearchBox
              options={DefaultDates[activeTab]?.trendDateOptions || []}
              isMany={true}
              placeholder={
                DefaultDates[activeTab]?.datePlaceholder || "Date Range"
              }
              selectedOptions={DefaultDates[activeTab]?.defaultTrendDates || []}
              onChange={(item: any) => {
                let dates = [...item];
                let i = 0;
                while (i < dates.length) {
                  if (dates[i].label.includes("to")) {
                    const labelToRemove = dates[i].label;
                    dates = dates.filter((d: any) => d.label !== labelToRemove);
                  } else {
                    i++;
                  }
                }

                dispatch(
                  updateDefaultTrendDates({
                    activeTab,
                    dates: dates,
                  })
                );
              }}
            />
          )}
        </div>
      </div>

      {activeTab && (
        <div className="tabContent">
          <div key={activeTab}>
            {tabs[activeTab] || <div>Invalid tab selected</div>}
          </div>
        </div>
      )}
    </div>
  );
}
